// src/pages/admin/AdminNotice.jsx
import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import "../Education.css";

const CATEGORIES = ["重要資訊", "衛教新知", "醫療服務", "人才招募", "活動相關"];
const BUCKET = "notices";

function catStyle(cat) {
  switch (cat) {
    case "重要資訊":
      return { bg: "#fee2e2", bd: "#fecaca", fg: "#991b1b" };
    case "衛教新知":
      return { bg: "#fef9c3", bd: "#fde68a", fg: "#92400e" };
    case "醫療服務":
      return { bg: "#e0f2fe", bd: "#bae6fd", fg: "#0c4a6e" };
    case "人才招募":
      return { bg: "#ede9fe", bd: "#ddd6fe", fg: "#5b21b6" };
    case "活動相關":
      return { bg: "#dcfce7", bd: "#bbf7d0", fg: "#166534" };
    default:
      return { bg: "#f1f5f9", bd: "#e2e8f0", fg: "#334155" };
  }
}

export default function AdminNotice() {
  // --- form/list state ---
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [author, setAuthor] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [publishedAt, setPublishedAt] = useState(() =>
    toLocalDatetimeInput(new Date())
  );
  const [file, setFile] = useState(null);
  const [removeImage, setRemoveImage] = useState(false); // 勾選後更新時會把 image_url 清掉
  const [editingId, setEditingId] = useState(null); // null 表示新增模式

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [msg, setMsg] = useState("");
  const [msgType, setMsgType] = useState(null); // success | error | info

  // 與 Education 相同的 Markdown 呈現（link 新開、img 自適應、table 可捲動、code 樣式）
  const mdComponents = useMemo(
    () => ({
      a: ({ node, ...props }) => (
        <a {...props} target="_blank" rel="noopener noreferrer" />
      ),
      img: ({ node, ...props }) => (
        <img
          {...props}
          style={{
            maxWidth: "100%",
            borderRadius: 8,
            border: "1px solid #e5e7eb",
          }}
          loading="lazy"
          alt={props.alt || "image"}
        />
      ),
      table: ({ node, ...props }) => (
        <div
          className="md-table-scroll"
          role="region"
          aria-label="可左右捲動的表格"
        >
          <table {...props} className="md-table" />
        </div>
      ),
      th: ({ node, ...props }) => (
        <th
          {...props}
          style={{
            border: "1px solid #e5e7eb",
            padding: "8px 12px",
            background: "#f8fafc",
            fontWeight: "bold",
            textAlign: "center",
          }}
        />
      ),
      td: ({ node, ...props }) => (
        <td
          {...props}
          style={{
            border: "1px solid #e5e7eb",
            padding: "8px 12px",
            textAlign: "center",
          }}
        />
      ),
      code: ({ inline, children, ...props }) => {
        const text = String(children || "");
        const onlySpaces = text.replace(/\s/g, "") === "";
        if (onlySpaces) return inline ? <span /> : <br />;
        return inline ? (
          <code
            {...props}
            style={{ background: "#f1f5f9", borderRadius: 4, padding: "0 4px" }}
          >
            {children}
          </code>
        ) : (
          <pre
            style={{
              background: "#0b1220",
              color: "#e5e7eb",
              borderRadius: 8,
              padding: 12,
              overflow: "auto",
            }}
          >
            <code {...props}>{children}</code>
          </pre>
        );
      },
    }),
    []
  );
  // --- styles ---
  const styles = useMemo(
    () => ({
      wrap: { maxWidth: 960, margin: "24px auto", padding: "0 16px" },
      h2: { margin: "0 0 16px", fontSize: 22, fontWeight: 800 },
      row: { display: "grid", gap: 8, marginBottom: 10 },
      label: { fontSize: 13, color: "#374151", fontWeight: 600 },
      control: {
        height: 40,
        padding: "8px 10px",
        borderRadius: 10,
        border: "1px solid #d1d5db",
        outline: "none",
      },
      btnPrimary: {
        height: 40,
        padding: "0 14px",
        borderRadius: 10,
        border: "1px solid #0ea5e9",
        background: "#0ea5e9",
        color: "#fff",
        cursor: "pointer",
      },
      msg: (t) => ({
        marginTop: 10,
        padding: "8px 10px",
        borderRadius: 8,
        border: "1px solid",
        ...(t === "success"
          ? { background: "#ecfdf5", borderColor: "#6ee7b7", color: "#065f46" }
          : t === "error"
          ? { background: "#fef2f2", borderColor: "#fecaca", color: "#991b1b" }
          : {
              background: "#f1f5f9",
              borderColor: "#e2e8f0",
              color: "#334155",
            }),
      }),
      // admin form
      form: {
        display: "grid",
        gap: 12,
        padding: 12,
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        background: "#fff",
      },
      two: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
      textarea: {
        minHeight: 100,
        padding: "10px",
        borderRadius: 10,
        border: "1px solid #d1d5db",
        outline: "none",
        resize: "vertical",
      },
      actions: {
        display: "flex",
        gap: 10,
        justifyContent: "space-between",
        alignItems: "center",
      },
      right: { display: "flex", gap: 10 },
      listCard: {
        marginTop: 16,
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        background: "#fff",
        overflow: "hidden",
      },
      listHead: {
        padding: "10px 12px",
        borderBottom: "1px solid #e5e7eb",
        background: "#f8fafc",
        fontWeight: 700,
      },
      item: {
        display: "grid",
        gridTemplateColumns: "1fr minmax(240px, 260px)",
        gap: 12,
        padding: "12px",
        borderBottom: "1px solid #f1f5f9",
      },
      leftCol: { minWidth: 0 },
      /* 右欄：不允許縮小，必要時可在小螢幕自動換行堆疊 */
      rightCol: {
        display: "flex",
        alignItems: "start",
        gap: 10,
        flexShrink: 0,
        flexWrap: "wrap",
      },
      catBadge: (c) => {
        const s = catStyle(c);
        return {
          display: "inline-block",
          padding: "2px 8px",
          borderRadius: 999,
          background: s.bg,
          border: `1px solid ${s.bd}`,
          color: s.fg,
          fontSize: 12,
          fontWeight: 700,
          marginRight: 8,
        };
      },
      titleLine: { fontSize: 16, fontWeight: 700, color: "#111827" },
      meta: { fontSize: 12, color: "#6b7280" },
      contentBox: (c) => {
        const s = catStyle(c);
        return {
          marginTop: 6,
          fontSize: 14,
          color: s.fg,
          whiteSpace: "pre-wrap",
          background: s.bg,
          border: `1px solid ${s.bd}`,
          borderRadius: 8,
          padding: 10,
          /* 避免超長單字/網址把版面撐破 */
          overflowWrap: "anywhere",
          wordBreak: "break-word",
        };
      },
      thumb: {
        marginTop: 8,
        maxWidth: 160,
        borderRadius: 8,
        border: "1px solid #e5e7eb",
      },
      delBtn: {
        height: 36,
        padding: "0 12px",
        borderRadius: 8,
        border: "1px solid #ef4444",
        background: "#ef4444",
        color: "#fff",
        cursor: "pointer",
      },
      toggleBtn: {
        height: 36,
        padding: "0 12px",
        borderRadius: 8,
        border: "1px solid #64748b",
        background: "#64748b",
        color: "#fff",
        cursor: "pointer",
        marginRight: 8,
      },
    }),
    []
  );

  // --- data ops ---
  const fetchNotices = async () => {
    setLoading(true);
    setMsg("");
    setMsgType(null);
    const { data, error } = await supabase
      .from("notices")
      .select("*")
      .order("published_at", { ascending: false })
      .limit(300);
    if (error) {
      console.error(error);
      setMsg("讀取失敗");
      setMsgType("error");
    } else setRows(data || []);
    setLoading(false);
  };

  // 進頁就拉資料（此頁已被 AdminLayout 保護，保證已登入）
  useEffect(() => {
    fetchNotices();
  }, []);

  const onCreate = async () => {
    if (!title.trim() || !content.trim()) {
      setMsg("請填寫標題與內容");
      setMsgType("info");
      return;
    }

    setLoading(true);
    setMsg("");
    setMsgType(null);
    let image_url = null;

    try {
      if (file) {
        const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
        const path = `covers/n_${Date.now()}.${ext}`;
        const up = await supabase.storage.from(BUCKET).upload(path, file, {
          cacheControl: "3600",
          upsert: false,
          // 某些瀏覽器上傳 HEIC/未知副檔名會是空字串，給保底值避免 400
          contentType:
            file.type && file.type.trim()
              ? file.type
              : "application/octet-stream",
        });
        if (up.error) throw up.error;
        const pub = supabase.storage.from(BUCKET).getPublicUrl(path);
        image_url = pub.data.publicUrl;
      }

      const iso = toIsoFromLocalInput(publishedAt) || new Date().toISOString();
      const { error } = await supabase.from("notices").insert({
        title: title.trim(),
        content: content.trim(),
        category,
        author: author.trim() || null,
        is_active: isActive,
        published_at: iso,
        image_url,
      });
      if (error) throw error;

      setMsg("✅ 已新增公告");
      setMsgType("success");
      setTitle("");
      setContent("");
      setAuthor("");
      setFile(null);
      setPublishedAt(toLocalDatetimeInput(new Date()));
      fetchNotices();
    } catch (err) {
      console.error(err);
      // 直接把 Storage/Supabase 的訊息帶出來，除錯更快
      setMsg(`新增失敗：${err?.message || "未知錯誤"}`);
      setMsgType("error");
    } finally {
      setLoading(false);
    }
  };

  const onEdit = (row) => {
    setEditingId(row.id);
    setTitle(row.title || "");
    setContent(row.content || "");
    setCategory(row.category || CATEGORIES[0]);
    setAuthor(row.author || "");
    setIsActive(!!row.is_active);
    setPublishedAt(toLocalDatetimeInput(new Date(row.published_at)));
    setFile(null);
    setRemoveImage(false);
    setMsg("");
    setMsgType(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onCancelEdit = () => {
    setEditingId(null);
    setTitle("");
    setContent("");
    setCategory(CATEGORIES[0]);
    setAuthor("");
    setIsActive(true);
    setPublishedAt(toLocalDatetimeInput(new Date()));
    setFile(null);
    setRemoveImage(false);
    setMsg("");
    setMsgType(null);
  };

  const onUpdate = async () => {
    if (!editingId) return;
    if (!title.trim() || !content.trim()) {
      setMsg("請填寫標題與內容");
      setMsgType("info");
      return;
    }
    setLoading(true);
    setMsg("");
    setMsgType(null);
    try {
      let nextImageUrl = null;
      // 找出舊資料（要處理保留舊圖的情況）
      const old = rows.find((r) => r.id === editingId) || {};
      nextImageUrl = old.image_url || null;

      if (removeImage) {
        nextImageUrl = null;
      } else if (file) {
        const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
        const path = `covers/n_${Date.now()}.${ext}`;
        const up = await supabase.storage.from(BUCKET).upload(path, file, {
          cacheControl: "3600",
          upsert: false,
          contentType:
            file.type && file.type.trim()
              ? file.type
              : "application/octet-stream",
        });
        if (up.error) throw up.error;
        const pub = supabase.storage.from(BUCKET).getPublicUrl(path);
        nextImageUrl = pub.data.publicUrl;
      }

      const iso = toIsoFromLocalInput(publishedAt) || new Date().toISOString();
      const { error } = await supabase
        .from("notices")
        .update({
          title: title.trim(),
          content: content.trim(),
          category,
          author: author.trim() || null,
          is_active: isActive,
          published_at: iso,
          image_url: nextImageUrl,
        })
        .eq("id", editingId);
      if (error) throw error;

      setMsg("✅ 已更新公告");
      setMsgType("success");
      await fetchNotices();
      onCancelEdit();
    } catch (err) {
      console.error(err);
      setMsg(`更新失敗：${err?.message || "未知錯誤"}`);
      setMsgType("error");
    } finally {
      setLoading(false);
    }
  };

  const onDelete = async (row) => {
    if (!window.confirm("確定刪除此公告嗎？")) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from("notices")
        .delete()
        .eq("id", row.id);
      if (error) throw error;
      setRows((rs) => rs.filter((r) => r.id !== row.id));
      setMsg("✅ 已刪除公告");
      setMsgType("success");

      // 可選：同步刪 Storage 圖
      if (row.image_url) {
        try {
          const u = new URL(row.image_url);
          // 例：/storage/v1/object/public/notices/covers/n_....jpg
          const parts = u.pathname.split(`/storage/v1/object/`);
          if (parts[1]) {
            const tail = parts[1]; // public/notices/...
            const afterBucket = tail.split(`${BUCKET}/`)[1]; // covers/xxx.jpg
            if (afterBucket) {
              await supabase.storage.from(BUCKET).remove([afterBucket]);
            }
          }
        } catch {}
      }
    } catch (err) {
      console.error(err);
      setMsg("刪除失敗");
      setMsgType("error");
    } finally {
      setLoading(false);
    }
  };

  const onToggleActive = async (row) => {
    setLoading(true);
    const { error } = await supabase
      .from("notices")
      .update({ is_active: !row.is_active })
      .eq("id", row.id);
    if (error) {
      console.error(error);
      setMsg("更新狀態失敗");
      setMsgType("error");
    } else {
      setRows((rs) =>
        rs.map((r) => (r.id === row.id ? { ...r, is_active: !r.is_active } : r))
      );
      setMsg("✅ 已更新狀態");
      setMsgType("success");
    }
    setLoading(false);
  };

  return (
    <div style={styles.wrap}>
      <h2 style={styles.h2}>公告管理</h2>

      {/* 建立公告 */}
      <div style={styles.form}>
        <div style={styles.row}>
          <label style={styles.label}>標題</label>
          <input
            style={styles.control}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="例：流感疫苗開始施打囉！"
            disabled={loading}
          />
        </div>

        <div style={styles.two}>
          <div style={styles.row}>
            <label style={styles.label}>類別</label>
            <select
              style={styles.control}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              disabled={loading}
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div style={styles.row}>
            <label style={styles.label}>作者</label>
            <input
              style={styles.control}
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              placeholder="例：李醫師 / 院辦"
              disabled={loading}
            />
          </div>
        </div>

        <div style={styles.two}>
          <div style={styles.row}>
            <label style={styles.label}>發布時間</label>
            <input
              type="datetime-local"
              style={styles.control}
              value={publishedAt}
              onChange={(e) => setPublishedAt(e.target.value)}
              disabled={loading}
            />
          </div>
          <div style={styles.row}>
            <label style={styles.label}>是否顯示</label>
            <select
              style={styles.control}
              value={isActive ? "1" : "0"}
              onChange={(e) => setIsActive(e.target.value === "1")}
              disabled={loading}
            >
              <option value="1">顯示</option>
              <option value="0">隱藏</option>
            </select>
          </div>
        </div>

        <div style={styles.row}>
          <label style={styles.label}>上傳相關圖片（可選）</label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            disabled={loading}
          />
        </div>

        {editingId && (
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: 6,
            }}
          >
            <input
              type="checkbox"
              checked={removeImage}
              onChange={(e) => setRemoveImage(e.target.checked)}
              disabled={loading}
            />
            <span>移除目前圖片</span>
          </label>
        )}

        <div style={styles.row}>
          <label style={styles.label}>內容</label>
          <textarea
            style={styles.textarea}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="公告內容…"
            disabled={loading}
          />
        </div>
        {/* Markdown 預覽（安全渲染） */}
        <div
          style={{ border: "1px dashed #E5E7EB", borderRadius: 8, padding: 10 }}
        >
          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>
            預覽（Markdown）
          </div>
          <div className="markdown-body">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw, rehypeSanitize]}
              components={mdComponents}
            >
              {content || "_（無內容）_"}
            </ReactMarkdown>
          </div>
        </div>

        <div style={styles.actions}>
          <div style={{ fontSize: 12, color: "#6b7280" }}>
            上傳圖片會存入 Storage bucket：<code>{BUCKET}</code>
          </div>
          <div style={styles.right}>
            {editingId ? (
              <>
                <button
                  style={styles.btnPrimary}
                  onClick={onUpdate}
                  disabled={loading}
                >
                  {loading ? "處理中…" : "更新公告"}
                </button>
                <button
                  className="btn ghost"
                  onClick={onCancelEdit}
                  disabled={loading}
                >
                  取消編輯
                </button>
              </>
            ) : (
              <button
                style={styles.btnPrimary}
                onClick={onCreate}
                disabled={loading}
              >
                {loading ? "處理中…" : "新增公告"}
              </button>
            )}
          </div>
        </div>

        {msg && <div style={styles.msg(msgType)}>{msg}</div>}
      </div>

      {/* 列表 */}
      <div style={styles.listCard}>
        <div style={styles.listHead}>公告列表（最新在上）</div>
        {rows.length === 0 ? (
          <div style={{ padding: 16, color: "#6b7280" }}>尚無公告</div>
        ) : (
          rows.map((r) => {
            const s = catStyle(r.category);
            return (
              <div key={r.id} style={styles.item}>
                <div style={styles.leftCol}>
                  <div style={styles.titleLine}>
                    <span style={styles.catBadge(r.category)}>
                      {r.category}
                    </span>
                    {r.title}
                  </div>
                  <div style={styles.meta}>
                    發布：{fmtDate(r.published_at)}　作者：{r.author || "—"}
                    　狀態：{r.is_active ? "顯示中" : "隱藏"}
                  </div>
                  {r.image_url && (
                    <img alt="notice" src={r.image_url} style={styles.thumb} />
                  )}
                  <div
                    style={styles.contentBox(r.category)}
                    className="markdown-body"
                  >
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeRaw, rehypeSanitize]}
                      components={mdComponents}
                    >
                      {r.content || ""}
                    </ReactMarkdown>
                  </div>
                </div>
                <div style={styles.rightCol}>
                  <button
                    style={{
                      ...styles.toggleBtn,
                      background: "#0f766e",
                      borderColor: "#0f766e",
                    }}
                    onClick={() => onEdit(r)}
                    disabled={loading}
                  >
                    編輯
                  </button>
                  <button
                    style={styles.toggleBtn}
                    onClick={() => onToggleActive(r)}
                    disabled={loading}
                  >
                    {r.is_active ? "設為隱藏" : "設為顯示"}
                  </button>
                  <button
                    style={styles.delBtn}
                    onClick={() => onDelete(r)}
                    disabled={loading}
                  >
                    刪除
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

/* helpers */
function toLocalDatetimeInput(d) {
  const y = d.getFullYear(),
    m = String(d.getMonth() + 1).padStart(2, "0"),
    da = String(d.getDate()).padStart(2, "0");
  const h = String(d.getHours()).padStart(2, "0"),
    mi = String(d.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${da}T${h}:${mi}`;
}
function toIsoFromLocalInput(v) {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d.toISOString();
}
function fmtDate(iso) {
  try {
    const d = new Date(iso);
    return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(
      2,
      "0"
    )}/${String(d.getDate()).padStart(2, "0")}`;
  } catch {
    return iso;
  }
}
